declare global {
  interface Window {
    google?: any;
  }
}

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const MAPS_API_URL = 'https://maps.googleapis.com/maps/api/js';
const WATERLOO_LOCATION = { lat: 43.4643, lng: -80.5204 };
const AUTOCOMPLETE_BIAS_RADIUS_METERS = 50000;
const ROUTE_DEPARTURE_BUFFER_MS = 2 * 60 * 1000;
const requestedLibraries = new Set<string>();
let bootstrapPromise: Promise<void> | null = null;
let placesAutocompleteDisabledByError = false;

export function hasMapsKey(): boolean {
  return API_KEY.length > 0;
}

export function hasPlacesAutocomplete(): boolean {
  return hasMapsKey() && !placesAutocompleteDisabledByError;
}

function getGoogleMapsImportLibrary(): (libraryName: string) => Promise<any> {
  if (typeof window === 'undefined') {
    throw new Error('Window not available');
  }
  if (!API_KEY) {
    throw new Error('Missing REACT_APP_GOOGLE_MAPS_API_KEY');
  }

  const google = window.google || (window.google = {});
  const maps = google.maps || (google.maps = {});
  if (typeof maps.importLibrary === 'function') {
    return maps.importLibrary.bind(maps);
  }

  const bootstrapImportLibrary = (libraryName: string) => {
    requestedLibraries.add(libraryName);
    if (!bootstrapPromise) {
      bootstrapPromise = new Promise((resolve, reject) => {
        const params = new URLSearchParams();
        params.set('key', API_KEY);
        params.set('v', 'weekly');
        params.set('loading', 'async');
        params.set('libraries', Array.from(requestedLibraries).join(','));
        params.set('callback', 'google.maps.__departGoogleMapsLoaded');

        maps.__departGoogleMapsLoaded = () => {
          delete maps.__departGoogleMapsLoaded;
          resolve();
        };

        const script = document.createElement('script');
        script.src = `${MAPS_API_URL}?${params.toString()}`;
        script.async = true;
        script.onerror = () => {
          bootstrapPromise = null;
          reject(new Error('Failed to load Google Maps'));
        };
        document.head.appendChild(script);
      });
    }

    return bootstrapPromise.then(() => {
      if (typeof maps.importLibrary !== 'function' || maps.importLibrary === bootstrapImportLibrary) {
        throw new Error('Google Maps loaded without dynamic library support. Reload the page and try again.');
      }
      return maps.importLibrary(libraryName);
    });
  };

  maps.importLibrary = bootstrapImportLibrary;
  return maps.importLibrary;
}

export async function loadGoogleMaps(): Promise<any> {
  await importMapsLibrary('core');
  return window.google;
}

async function importMapsLibrary(libraryName: string): Promise<any> {
  const importLibrary = getGoogleMapsImportLibrary();
  return importLibrary(libraryName);
}

function predictionText(prediction: any): string {
  if (prediction.text && typeof prediction.text.toString === 'function') {
    return prediction.text.toString();
  }
  return prediction.text && prediction.text.text ? prediction.text.text : '';
}

function isPermissionError(err: any): boolean {
  const message = String((err && (err.message || err.status || err.code || err.name)) || '').toLowerCase();
  return message.includes('403')
    || message.includes('permission')
    || message.includes('request_denied')
    || message.includes('api_not_activated')
    || message.includes('not authorized');
}

export interface PlacePrediction {
  description: string;
  placeId: string;
  distanceMeters?: number;
}

function predictionDistance(prediction: PlacePrediction): number {
  return typeof prediction.distanceMeters === 'number'
    ? prediction.distanceMeters
    : Number.POSITIVE_INFINITY;
}

export async function fetchPlacePredictions(input: string): Promise<PlacePrediction[]> {
  if (!input.trim() || !hasPlacesAutocomplete()) return [];
  try {
    const { AutocompleteSuggestion } = await importMapsLibrary('places');
    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input,
      includedRegionCodes: ['ca'],
      locationBias: {
        center: WATERLOO_LOCATION,
        radius: AUTOCOMPLETE_BIAS_RADIUS_METERS,
      },
      origin: WATERLOO_LOCATION,
      region: 'ca',
    });
    return (suggestions || [])
      .map((suggestion: any) => suggestion.placePrediction)
      .filter(Boolean)
      .map((prediction: any) => ({
        description: predictionText(prediction),
        distanceMeters: prediction.distanceMeters,
        placeId: prediction.placeId,
      }))
      .filter((prediction: PlacePrediction) => prediction.description)
      .sort((a: PlacePrediction, b: PlacePrediction) => predictionDistance(a) - predictionDistance(b));
  } catch (err) {
    if (isPermissionError(err)) {
      placesAutocompleteDisabledByError = true;
      console.warn('Places autocomplete disabled: enable Places API (New) for this key/project.');
      return [];
    }
    throw err;
  }
}

export interface DrivingResult {
  minutes: number;
  durationText: string;
  distanceText: string;
}

export async function fetchDrivingTime(origin: string, destination: string): Promise<DrivingResult> {
  const departureTime = new Date(Date.now() + ROUTE_DEPARTURE_BUFFER_MS);
  const { Route } = await importMapsLibrary('routes');
  const { routes } = await Route.computeRoutes({
    origin,
    destination,
    travelMode: 'DRIVING',
    routingPreference: 'TRAFFIC_AWARE',
    departureTime,
    fields: ['durationMillis', 'distanceMeters', 'localizedValues'],
  });
  const route = routes && routes[0];
  if (!route || typeof route.durationMillis !== 'number') {
    throw new Error('No route found');
  }
  const distanceText = route.distanceMeters
    ? `${(route.distanceMeters / 1609.344).toFixed(1)} mi`
    : '';
  return {
    minutes: Math.round(route.durationMillis / 60000),
    durationText: route.localizedValues && route.localizedValues.duration
      ? route.localizedValues.duration
      : `${Math.round(route.durationMillis / 60000)} min`,
    distanceText,
  };
}
